/**
 * Plain-language server errors (ADR-0065).
 *
 * Services answer with technical details ("case was modified concurrently (version mismatch)",
 * "requires one of roles ['case-supervisor']", "title.en: String should have at most 2000
 * characters", "audit unavailable: …"). The console shows a plain sentence for the *kind* of
 * error (by HTTP status), a plain line for every detail it recognises (`ERROR_TEXT_RULES`, i18n
 * `pickers:error.detail.<key>`), policy refusals through `reasons.ts`, and keeps the raw text in
 * a collapsed "Technical details" section.
 */
import { ApiError } from "./http";
import { apiErrorCode, explainReason, reasonsFrom, TOKEN_REFRESH_NEEDED } from "./reasons";

export type ErrorKind =
  | "offline"
  | "signed_out"
  | "forbidden"
  | "not_found"
  | "conflict"
  | "invalid"
  | "too_large"
  | "unsupported"
  | "rate_limited"
  | "unavailable"
  | "server"
  | "unknown";

export interface ErrorTextRule {
  match: string | RegExp;
  key: string;
  /** Names of the regex groups, in order (values for the i18n text). */
  params?: string[];
}

export interface ExplainedDetail {
  /** `pickers:error.detail.<key>`, or null when only a policy reason or raw text applies. */
  key: string | null;
  /** A policy reason key (`clarity:reason.<key>`), when the detail is a policy refusal. */
  reasonKey: string | null;
  values: Record<string, string>;
  raw: string;
}

export interface PlainError {
  kind: ErrorKind;
  status: number | null;
  details: ExplainedDetail[];
  /** The service's full message, for Technical details. */
  raw: string;
}

const e = (match: string | RegExp, key: string, params?: string[]): ErrorTextRule => ({ match, key, ...(params ? { params } : {}) });

/** Common details of the cases, identity, control-plane, docintel, memory and gateway services. */
export const ERROR_TEXT_RULES: ErrorTextRule[] = [
  // ---- not found / gone -----------------------------------------------------------------------
  e(/^(case|task|user|agent|document|job|collection|listing|promotion|case type|model|tool) not found$/i, "not_found_thing", ["thing"]),
  e(/^agent (\S+) not found$/, "not_found_thing", ["thing"]),
  // ---- concurrency ----------------------------------------------------------------------------
  e("case was modified concurrently (version mismatch)", "changed_meanwhile"),
  e("a concurrent request created this task; retry", "changed_meanwhile"),
  e(/^task is already claimed by another reviewer$|^task is claimed by another reviewer$/, "claimed_by_other"),
  e(/^task is (?:already )?(done|cancelled|escalated|open|claimed)(?:, not claimed)?$/, "task_state", ["status"]),
  e(/^invalid status transition (\w+) -> (\w+)$/, "status_transition", ["from", "to"]),
  e("a user with this username or email exists", "user_exists"),
  e("this version is already deployed", "already_deployed"),
  e(/^promote to (\w+) first$/, "promote_first", ["env"]),
  e("case type is used by cases; switch it off instead", "case_type_in_use"),
  // ---- who may act ----------------------------------------------------------------------------
  e(/^requires one of roles \[(.+)\]$/, "needs_role", ["roles"]),
  e(/^requires role '([^']+)'$/, "needs_role", ["roles"]),
  e(/^requires role '([^']+)' or '([^']+)'$/, "needs_role", ["roles", "roles2"]),
  e("not a participant of this case", "not_participant"),
  e("you do not hold a role of this task's queue", "not_in_queue"),
  e(/^only the assignee or a case-supervisor can (reassign|release a task)$/, "assignee_or_supervisor"),
  e("reopening a closed case requires case-supervisor", "reopen_supervisor"),
  e("clearance below the case classification", "clearance"),
  e(/^requires a human user/, "human_only"),
  e(/^(?:cross-tenant .+ is not allowed|cross-tenant access is not allowed)$/, "other_tenant"),
  // ---- what was sent --------------------------------------------------------------------------
  e(/^missing required information: (.+)$/, "missing_field", ["field"]),
  e(/^(.+) must be a number$/, "must_be_number", ["field"]),
  e(/^(.+) must be a date \(YYYY-MM-DD\)$/, "must_be_date", ["field"]),
  e(/^case type '(.+)' is switched off$/, "case_type_off", ["type"]),
  e(/^unknown time zone '(.+)'$/, "unknown_timezone", ["zone"]),
  e(/day_start must be before day_end/, "hours_order"),
  e(/at least one working day is required/, "no_working_days"),
  e("edit decisions must include edits", "edits_required"),
  e("approval decisions require a signed approval", "signature_required"),
  e("input tasks cannot be rejected; reassign or escalate", "input_no_reject"),
  e("give an assignee, a queue, or both", "assignee_or_queue"),
  e("provide exactly one of file or uri", "one_file"),
  e("unknown document_type", "invalid_generic"),
  e(/^roles not assignable: (.+)$/, "roles_not_assignable", ["roles"]),
  e(/email is not a valid address/, "email_invalid"),
  e(/^username must match/, "username_invalid"),
  e(/^rejected by the identity provider/, "idp_rejected"),
  e(/^(?:Field required|field required)$/, "field_required"),
  e(/^String should have at most (\d+) characters?$/, "too_long", ["n"]),
  e(/^String should have at least (\d+) characters?$/, "too_short", ["n"]),
  e(/^String should match pattern/, "wrong_format"),
  e(/^Input should be a valid (number|integer)/, "must_be_number_generic"),
  e(/^Input should be greater than (?:or equal to )?(\S+)$/, "too_small", ["n"]),
  e(/^Input should be less than (?:or equal to )?(\S+)$/, "too_big", ["n"]),
  e(/^Value error, (.+)$/, "value_error", ["text"]),
  // ---- the platform -----------------------------------------------------------------------------
  e(/^audit unavailable/, "audit_down"),
  e(/^identity provider error$/, "idp_down"),
  e(/(?:keycloak|opa|policy engine) (?:down|unavailable)/i, "service_down"),
  e(/^HTTP 50\d$|^Bad Gateway$|^Service Unavailable$|^Gateway Timeout$/, "service_down"),
];

const KIND_BY_STATUS: Record<number, ErrorKind> = {
  401: "signed_out",
  403: "forbidden",
  404: "not_found",
  409: "conflict",
  410: "not_found",
  412: "conflict",
  413: "too_large",
  415: "unsupported",
  422: "invalid",
  400: "invalid",
  429: "rate_limited",
  502: "unavailable",
  503: "unavailable",
  504: "unavailable",
};

function kindOf(err: unknown): { kind: ErrorKind; status: number | null } {
  if (err instanceof ApiError) {
    const kind = KIND_BY_STATUS[err.status] ?? (err.status >= 500 ? "server" : "unknown");
    return { kind, status: err.status };
  }
  // fetch() rejects with a TypeError when the network or the gateway is unreachable.
  if (err instanceof TypeError && /fetch|network|load failed/i.test(err.message)) return { kind: "offline", status: null };
  return { kind: "unknown", status: null };
}

/** One detail ("loc: msg" for validation errors) → its rule, or a policy reason, or raw. */
export function explainDetail(raw: string): ExplainedDetail {
  const text = raw.trim();
  // FastAPI validation: "body.title.en: String should have …" — explain the message part.
  const loc = /^((?:[A-Za-z_][\w-]*|\d+)(?:\.(?:[A-Za-z_][\w-]*|\d+))*): (.+)$/.exec(text);
  const message = loc && !/^https?$/.test(loc[1] ?? "") ? (loc[2] as string) : text;
  const field = loc ? (loc[1] as string).split(".").filter((x) => !/^\d+$/.test(x)).pop() ?? "" : "";
  for (const rule of ERROR_TEXT_RULES) {
    let values: Record<string, string> | null = null;
    if (typeof rule.match === "string") {
      if (rule.match === message) values = {};
    } else {
      const m = rule.match.exec(message);
      if (m) {
        values = {};
        (rule.params ?? []).forEach((p, i) => {
          (values as Record<string, string>)[p] = (m[i + 1] ?? "").replace(/'/g, "");
        });
      }
    }
    if (values) return { key: rule.key, reasonKey: null, values: field ? { ...values, at: field } : values, raw: text };
  }
  const reason = explainReason(message);
  if (reason) return { key: null, reasonKey: reason.key, values: reason.values, raw: text };
  return { key: null, reasonKey: null, values: field ? { at: field } : {}, raw: text };
}

/** Plain explanation of any error thrown by the API client (or anything else). */
export function explainError(err: unknown): PlainError {
  const { kind, status } = kindOf(err);
  const raw = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  // The sign-in predates an agent's release (orchestrator 409 after one silent renewal failed).
  if (apiErrorCode(err) === TOKEN_REFRESH_NEEDED) return { kind, status, details: [{ key: null, reasonKey: TOKEN_REFRESH_NEEDED, values: {}, raw }], raw };
  // Policy refusals: "denied by policy: a; b" / {detail: {reasons}} → individual reasons.
  const policy = /^(?:denied by policy|policy denied|forbidden)\s*:/i.test(raw.trim()) || /denied by policy|policy denied/i.test(raw) || (err instanceof ApiError && Array.isArray((err.body as { detail?: { reasons?: unknown } } | undefined)?.detail?.reasons));
  // Some messages contain "; " themselves: try the whole text before splitting it.
  const exact = !policy && ERROR_TEXT_RULES.some((r) => r.match === raw.trim());
  const whole = exact ? explainDetail(raw) : null;
  const parts = policy ? reasonsFrom(err) : raw.split(/;\s+/).map((s) => s.trim()).filter(Boolean);
  const details = whole?.key ? [whole] : parts.filter((p) => !/^denied by policy$/i.test(p)).map(explainDetail);
  return { kind: kind === "unknown" && details.some((d) => d.reasonKey) ? "forbidden" : kind, status, details, raw };
}
